import { useOrg } from '@/contexts/OrgContext'
import {
  useEngineStatus,
  type EngineStatus as OutreachEngineStatus,
} from '@/components/outreach/engine-status'

// ────────────────────────────────────────────────────────────────────────
// useOutreachEngineStatus — honest state of the outreach executor.
//
// The outreach engine is intentionally OFF (owner decision): the pg_cron job
// that consumes the schedule queue does not exist and the org-level kill
// switch (org_chief_settings.outreach_enabled) is false. Cadence surfaces
// must NOT imply that scheduled steps will run. This hook reads the REAL
// state via the `outreach-engine-status` edge fn (CA-003) so the UI can show
// an honest banner instead of assuming a running engine.
//
// CA-062: this used to run its OWN useQuery on the ['outreach-engine-status',
// orgId] key with a different queryFn/auth than useEngineStatus — two hooks
// fighting over ONE TanStack cache entry (last writer wins, auth mismatch).
// It is now a thin adapter over the single source of truth in
// `components/outreach/engine-status.ts`, so every consumer (cadence surfaces,
// the outreach banner and the control page) shares exactly one query.
//
// No writes here. It never toggles the switch or touches crons — turning the
// engine back on lives in the (admin-only) OutreachControl surface.
// ────────────────────────────────────────────────────────────────────────

export type { OutreachEngineStatus }

export function useOutreachEngineStatus() {
  const { orgId } = useOrg()
  const query = useEngineStatus(orgId)

  return {
    status: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    /** True only when we KNOW the engine is running; false while loading/unknown. */
    engineOn: query.data?.engine_healthy === true,
    /** True only when we have data AND it says the engine is off. */
    engineOff: !!query.data && query.data.engine_healthy !== true,
  }
}
